package one.devsbox.www.tools

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLVideoElement

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun VideoPlayer(
    modifier: Modifier = Modifier,
    videoUrl: String,
    isPlaying: Boolean,
    isFullscreen: Boolean,
    onClose: () -> Unit
) {
    Box(
        modifier = if (isFullscreen) Modifier.fillMaxSize() else modifier
    ) {
        WebElementView(
            factory = {
                // 1. Create the main wrapper container
                val container = document.createElement("div") as HTMLDivElement
                container.style.apply {
                    position = "relative"
                    width = "100%"
                    height = "100%"
                    backgroundColor = "black"
                }

                // 2. Hide native browser fullscreen controls via CSS injection
                val styleElement = document.createElement("style")
                styleElement.textContent = """
                    video::-webkit-media-controls-fullscreen-button {
                        display: none !important;
                    }
                    video::-internal-media-controls-fullscreen-button {
                        display: none !important;
                    }
                """.trimIndent()
                container.appendChild(styleElement)

                // 3. Create the video element
                val video = document.createElement("video") as HTMLVideoElement
                video.apply {
                    controls = true
                    style.width = "100%"
                    style.height = "100%"
                    style.display = "block"
                }
                container.appendChild(video)

                // 4. Create a BIGGER native HTML Close Button
                val closeButton = document.createElement("button") as HTMLButtonElement
                closeButton.id = "video-close-btn"

                closeButton.style.apply {
                    position = "absolute"
                    top = "24px"          // Slightly pushed out further from the edge
                    right = "24px"
                    width = "64px"         // Increased from 40px to 64px
                    height = "64px"        // Increased from 40px to 64px
                    borderRadius = "50%"
                    border = "none"
                    backgroundColor = "rgba(0, 0, 0, 0.7)" // Slightly darker backdrop for visibility
                    color = "white"
                    fontSize = "32px"      // Scaled up font-size for the '✕' symbol
                    cursor = "pointer"
                    display = "none"       // Hidden by default, controlled in update
                    alignItems = "center"
                    justifyContent = "center"
                    zIndex = "10000"
                    transition = "background-color 0.2s ease, transform 0.2s ease"
                }
                closeButton.innerHTML = "&#x2715;" // HTML Unicode for '✕' (Close Cross)

                // Native hover micro-interactions
                closeButton.onmouseover = {
                    closeButton.style.backgroundColor = "rgba(255, 0, 0, 0.8)" // Turns red on hover
                    closeButton.style.transform = "scale(1.08)"
                }
                closeButton.onmouseout = {
                    closeButton.style.backgroundColor = "rgba(0, 0, 0, 0.7)"
                    closeButton.style.transform = "scale(1.0)"
                }

                container.appendChild(closeButton)
                container
            },
            modifier = Modifier.fillMaxSize(),
            update = { container ->
                val video = container.querySelector("video") as? HTMLVideoElement ?: return@WebElementView
                val closeButton = container.querySelector("#video-close-btn") as? HTMLButtonElement

                // 5. Manage Fullscreen styles & Close Button Visibility entirely in DOM
                if (isFullscreen) {
                    container.style.apply {
                        position = "fixed"
                        top = "0"
                        left = "0"
                        width = "100vw"
                        height = "100vh"
                        zIndex = "9999"
                    }
                    closeButton?.style?.display = "flex" // Show button
                } else {
                    container.style.apply {
                        position = "relative"
                        width = "100%"
                        height = "100%"
                        zIndex = "auto"
                    }
                    closeButton?.style?.display = "none" // Hide button
                }

                // 6. Bind/Update the click listener cleanly to handle recompositions
                closeButton?.onclick = { event ->
                    event.stopPropagation() // Prevent event bubbling
                    onClose()
                }

                // 7. Sync video playback and source
                if (video.src != videoUrl) {
                    video.src = videoUrl
                }

                if (isPlaying) {
                    video.play()
                } else {
                    video.pause()
                }
            },
            onRelease = { container ->
                val video = container.querySelector("video") as? HTMLVideoElement
                val closeButton = container.querySelector("#video-close-btn") as? HTMLButtonElement

                // Housekeeping cleanly clears native references
                closeButton?.onclick = null
                closeButton?.onmouseover = null
                closeButton?.onmouseout = null

                video?.apply {
                    pause()
                    src = ""
                    load()
                }
            }
        )
    }
}